import * as Sentry from '@sentry/react';
import { getAccessToken } from '@privy-io/react-auth';
import { performLogout } from './auth-utils';

// Token refresh queue to prevent concurrent refresh attempts
let tokenRefreshPromise: Promise<string | null> | null = null;

// Retry configuration
const RETRY_ATTEMPTS = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // 1s, 2s, 4s backoff

// API Error types for proper error handling
class ApiError extends Error {
  public status: number;
  public statusText: string;
  public data?: unknown;

  constructor(
    status: number,
    statusText: string,
    message: string,
    data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

class NetworkError extends Error {
  constructor(message: string = 'Network connection failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

class AuthenticationError extends ApiError {
  constructor(message: string = 'Authentication required') {
    super(401, 'Unauthorized', message);
    this.name = 'AuthenticationError';
  }
}

class AuthorizationError extends ApiError {
  constructor(message: string = 'Insufficient permissions') {
    super(403, 'Forbidden', message);
    this.name = 'AuthorizationError';
  }
}

import { api as apiConfig } from '@/config';

// API Configuration
const API_BASE_URL = apiConfig.baseUrl;

// Request timeout for mobile networks (30 seconds)
const REQUEST_TIMEOUT = 30000;

// Helper function to attempt token refresh
async function attemptTokenRefresh(): Promise<string | null> {
  try {
    // Privy handles token refresh internally when we call getAccessToken
    const newToken = await getAccessToken();
    return newToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
}

// Helper function to get token with retry logic
async function getTokenWithRetry(): Promise<string | null> {
  // If there's already a refresh in progress, wait for it
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Start a new refresh attempt
  tokenRefreshPromise = attemptTokenRefresh();

  try {
    const token = await tokenRefreshPromise;
    return token;
  } finally {
    // Clear the promise after completion
    tokenRefreshPromise = null;
  }
}

// Helper function to delay for retry
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Core API client with authentication and error handling
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryAttempt: number = 0
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get token (use retry mechanism if this is a retry attempt)
  const token =
    retryAttempt > 0 ? await getTokenWithRetry() : await getAccessToken();

  // Build headers with authentication
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Backend expects Privy access token in 'privy-id-token' header
  if (token) {
    headers['privy-id-token'] = token;
  }

  // Create request with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle non-ok responses
    if (!response.ok) {
      const errorData = await response.text().catch(() => null);
      let errorMessage = `Request failed: ${response.status} ${response.statusText}`;

      try {
        const parsedError = errorData ? JSON.parse(errorData) : null;

        // Handle new nested error format
        if (parsedError?.detail?.error_detail) {
          errorMessage = parsedError.detail.error_detail;
        } else if (parsedError?.detail?.error_message) {
          errorMessage = parsedError.detail.error_message;
        } else if (parsedError?.error_detail) {
          // Legacy format support
          errorMessage = parsedError.error_detail;
        } else if (parsedError?.error_message) {
          // Legacy format support
          errorMessage = parsedError.error_message;
        } else {
          // Fallback to other common error fields
          errorMessage =
            parsedError?.message ||
            parsedError?.detail ||
            parsedError?.error ||
            errorMessage;
        }
      } catch {
        // Use response text as error message if JSON parsing fails
        errorMessage = errorData || errorMessage;
      }

      // Handle 401 errors with retry logic
      if (response.status === 401 && retryAttempt < RETRY_ATTEMPTS) {
        // Wait before retrying
        await delay(RETRY_DELAYS[retryAttempt]);

        // Retry the request
        return apiRequest<T>(endpoint, options, retryAttempt + 1);
      }

      // On final 401 failure, logout the user
      if (response.status === 401 && retryAttempt >= RETRY_ATTEMPTS) {
        // Trigger logout (debounced to prevent multiple simultaneous logouts)
        performLogout();
        throw new AuthenticationError(
          'Your session has expired. Please log in again.'
        );
      }

      // Throw specific error types for different status codes
      switch (response.status) {
        case 401:
          throw new AuthenticationError(errorMessage);
        case 403:
          throw new AuthorizationError(errorMessage);
        default:
          throw new ApiError(
            response.status,
            response.statusText,
            errorMessage
          );
      }
    }

    // Parse response
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return await response.json();
    }

    // Return text for non-JSON responses
    return (await response.text()) as T;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle network and timeout errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NetworkError('Request timeout');
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new NetworkError(
        'Network connection failed. Please check your connection.'
      );
    }

    // Re-throw API errors
    if (error instanceof ApiError) {
      throw error;
    }

    // Wrap unknown errors
    throw new NetworkError(
      error instanceof Error
        ? error.message
        : 'Something went wrong. Please try again later.'
    );
  }
}

// HTTP method helpers
export const api = {
  get: <T>(endpoint: string, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

// API error handler that reports to Sentry
export function handleApiError(
  error: unknown,
  context?: Record<string, unknown>
) {
  // Report to Sentry
  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error');
    if (context) {
      scope.setContext('api_context', context);
    }
    Sentry.captureException(error);
  });

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('API Error:', error, context);
  }
}

// Export error types for use in components
export { ApiError, NetworkError, AuthenticationError, AuthorizationError };
